import React, { useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import clsx from 'classnames';
import { Button } from '../../atoms/Button/Button';
import { Loader } from '../../atoms/Loader/Loader';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import './ToastStyles.scss';
// ─── Constants ───────────────────────────────────────────────────────────────
const TOAST_DURATION = {
    default: 5000,
    success: 5000,
    error: Infinity,
    info: 5000,
    loading: Infinity
};
const DEFAULT_ICONS = {
    success: React.createElement("i", { className: "fa-solid fa-badge-check" }),
    error: React.createElement("i", { className: "fa-solid fa-diamond-exclamation" }),
    info: React.createElement("i", { className: "fa-solid fa-circle-info" }),
    loading: React.createElement(Loader, { size: "24" })
};
// ─── Toaster Position ────────────────────────────────────────────────────────
let positionObserver = null;
/**
 * Watches the DOM for Brushaway panels and chat launchers (AI Chat Widget
 * or Intercom) and offsets the react-hot-toast container to avoid overlap.
 * Mirrors detection logic from useHideUserSupportBtn.
 * Starts automatically on first toast call, runs for the lifetime of the page.
 */
function ensureToasterPositioning() {
    if (positionObserver)
        return;
    function getLauncher() {
        // AI Chat Widget (same priority as useHideUserSupportBtn)
        const aiTrigger = document.getElementById('ai-trigger-button');
        if (aiTrigger && aiTrigger.style.visibility !== 'hidden')
            return aiTrigger;
        // Intercom (both states: default launcher and post-interaction)
        const intercom = document.querySelector('.intercom-lightweight-app') ||
            document.querySelector('.intercom-app');
        if (intercom && intercom.style.visibility !== 'hidden')
            return intercom;
        return null;
    }
    // react-hot-toast sets top/left/right/bottom: 16px on [data-rht-toaster]
    const DEFAULT_OFFSET = '16px';
    function update() {
        const toaster = document.querySelector('[data-rht-toaster]');
        if (!toaster)
            return;
        // Brushaway offset (right)
        const brushaway = document.querySelector('.qmb-ui-brushaway--active');
        toaster.style.right = brushaway ? `${brushaway.getBoundingClientRect().width + 16}px` : DEFAULT_OFFSET;
        // Launcher offset (bottom) — skip if brushaway is open (launchers are hidden)
        if (brushaway) {
            toaster.style.bottom = DEFAULT_OFFSET;
            return;
        }
        const launcher = getLauncher();
        toaster.style.bottom = launcher
            ? `${window.innerHeight - launcher.getBoundingClientRect().top + 16}px`
            : DEFAULT_OFFSET;
    }
    positionObserver = new MutationObserver(update);
    positionObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true
    });
    update();
}
// ─── Dismiss Animation ───────────────────────────────────────────────────────
/**
 * Runs the three-step exit animation on a toast element:
 * 1. Fade content (60ms)
 * 2. Collapse to 3px bar (140ms)
 * 3. Bar whooshes off to the right (200ms)
 */
function runDismissAnimation(el, toastId) {
    if (el.dataset.dismissing)
        return;
    el.dataset.dismissing = '1';
    // Fade all child content
    Array.from(el.children).forEach((child) => {
        const c = child;
        c.style.transition = 'opacity 60ms ease';
        c.style.opacity = '0';
    });
    // Lock dimensions
    const originalHeight = el.offsetHeight;
    el.style.overflow = 'hidden';
    el.style.boxShadow = 'none';
    el.style.transformOrigin = 'center center';
    // Collapse to bar
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            el.style.transition =
                'transform 140ms cubic-bezier(0.4, 0, 0.2, 1), background 140ms ease, border-radius 140ms ease';
            el.style.transform = `scaleY(${3 / originalHeight})`;
            el.style.background = 'rgba(79, 64, 120, 0.32)';
            el.style.borderRadius = '4px';
        });
    });
    // Whoosh off
    setTimeout(() => {
        el.style.transition = 'none';
        const scaleY = 3 / originalHeight;
        el.animate([
            { transform: `scaleY(${scaleY}) translateX(0) scaleX(1) rotate(0deg)`, opacity: 1 },
            { transform: `scaleY(${scaleY}) translateX(18px) scaleX(2.8) rotate(-0.6deg)`, opacity: 0 }
        ], { duration: 200, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }).onfinish = () => toast.remove(toastId);
    }, 155);
}
// ─── DOM Helpers ─────────────────────────────────────────────────────────────
function findToastEl(toastId) {
    return document.querySelector(`[data-toast-id="${toastId}"]`);
}
/**
 * Tags the react-hot-toast wrapper element with our data attributes,
 * variant class, duration variable, and QMB class structure.
 */
/**
 * Finds the react-hot-toast wrapper element for a toast by walking up
 * from a child node to the element containing [role="status"].
 */
function findToastWrapper(node) {
    let el = node;
    while (el) {
        if (el.querySelector(':scope > [role="status"]'))
            return el;
        el = el.parentElement;
    }
    return null;
}
/**
 * Tags the react-hot-toast wrapper element with our base class, data attributes,
 * variant class, duration variable, and QMB class structure.
 */
function tagToastEl(node, toastId, variantClass, duration) {
    const el = findToastWrapper(node);
    if (!el)
        return;
    el.classList.add('qmb-ui-toast');
    el.dataset.toastId = toastId;
    // A progress toast updates in place under a stable id, so it can change variant mid-life
    // (uploading -> failed). Drop any previous `qmb-ui-toast--*` before adding the current one, or
    // both variants' rules apply at once and the toast renders as a blend of the two.
    if (variantClass) {
        Array.from(el.classList)
            .filter((c) => c.startsWith('qmb-ui-toast--') && c !== variantClass)
            .forEach((c) => el.classList.remove(c));
        el.classList.add(variantClass);
    }
    if (duration && duration !== Infinity) {
        el.style.setProperty('--toast-duration', `${duration}ms`);
    }
    const statusEl = el.querySelector('[role="status"]');
    if (statusEl) {
        statusEl.classList.add('notification__body');
        statusEl.removeAttribute('style');
        requestAnimationFrame(() => statusEl.removeAttribute('style'));
    }
    const innerDiv = statusEl === null || statusEl === void 0 ? void 0 : statusEl.querySelector(':scope > div');
    if (innerDiv) {
        innerDiv.classList.add('notification__content');
        innerDiv.removeAttribute('style');
        requestAnimationFrame(() => innerDiv.removeAttribute('style'));
    }
}
// ─── Duration ────────────────────────────────────────────────────────────────
function resolveDuration(duration, variant) {
    if (duration === 0)
        return Infinity;
    if (duration === undefined)
        return TOAST_DURATION[variant];
    return duration;
}
export function ToastWrapper({ t, children, dismissable = true, variantClass, displayDuration }) {
    const animating = useRef(false);
    const buttonRef = useRef(null);
    const contentRef = useCallback((node) => {
        if (node)
            tagToastEl(node, t.id, variantClass, displayDuration);
    }, [t.id, variantClass, displayDuration]);
    useEffect(() => {
        if (buttonRef.current)
            tagToastEl(buttonRef.current, t.id, variantClass, displayDuration);
    }, [t.id, variantClass, displayDuration]);
    // Auto-dismiss via our own timer — same path as manual dismiss
    useEffect(() => {
        if (!displayDuration || displayDuration === Infinity)
            return undefined;
        const timer = setTimeout(() => {
            const el = findToastEl(t.id);
            if (el && !animating.current) {
                animating.current = true;
                runDismissAnimation(el, t.id);
            }
        }, displayDuration);
        return () => clearTimeout(timer);
    }, [t.id, displayDuration]);
    const handleDismiss = useCallback(() => {
        if (animating.current)
            return;
        animating.current = true;
        const el = findToastEl(t.id);
        if (el)
            runDismissAnimation(el, t.id);
    }, [t.id]);
    const wrappedChildren = typeof children === 'string' ? (React.createElement("span", { ref: contentRef }, children)) : React.isValidElement(children) ? (React.cloneElement(children, { ref: contentRef })) : (children);
    return (React.createElement(React.Fragment, null,
        wrappedChildren,
        dismissable && (React.createElement(Button, { ref: buttonRef, className: "notification__close", "aria-label": "Dismiss", onClick: handleDismiss },
            React.createElement("i", { className: "fa-light fa-xmark" })))));
}
ToastWrapper.displayName = 'ToastWrapper';
// ─── Internal Toast Renderer ─────────────────────────────────────────────────
function renderToast(message, variant, defaultIcon, variantClass, dismissable, options) {
    ensureToasterPositioning();
    const icon = (options === null || options === void 0 ? void 0 : options.icon) === null ? null : (options === null || options === void 0 ? void 0 : options.icon) || defaultIcon;
    const duration = resolveDuration(options === null || options === void 0 ? void 0 : options.duration, variant);
    return toast((t) => (React.createElement(ToastWrapper, { t: t, variantClass: variantClass, dismissable: dismissable, displayDuration: duration },
        React.createElement("span", { className: "notification__content" },
            icon && React.createElement("span", { className: "notification__icon" }, icon),
            message))), { duration: Infinity });
}
// ─── Public API ──────────────────────────────────────────────────────────────
/**
 * Show a default toast notification.
 *
 * @example
 * showToast('Something happened');
 * showToast('With icon', { icon: <i className="fa-solid fa-bell" /> });
 * showToast('Persistent', { duration: 0 });
 */
export function showToast(message, options) {
    return renderToast(message, 'default', null, undefined, true, options);
}
/**
 * Show a success toast. Green background, badge-check icon by default.
 * Duration: 5000ms.
 *
 * @example
 * showSuccessToast('Record saved');
 * showSuccessToast('Exported', { icon: <i className="fa-solid fa-download" /> });
 */
export function showSuccessToast(message, options) {
    return renderToast(message, 'success', DEFAULT_ICONS.success, 'qmb-ui-toast--success', true, options);
}
/**
 * Show an error toast. Red background, diamond-exclamation icon by default.
 * Persists until dismissed.
 *
 * @example
 * showErrorToast('Something went wrong');
 * showErrorToast('Connection lost', { duration: 0 }); // persistent
 */
export function showErrorToast(message, options) {
    return renderToast(message, 'error', DEFAULT_ICONS.error, 'qmb-ui-toast--error', true, options);
}
/**
 * Show an info toast. Blue background, circle-info icon by default.
 * Duration: 5000ms.
 *
 * @example
 * showInfoToast('Session expires in 5 minutes');
 */
export function showInfoToast(message, options) {
    return renderToast(message, 'info', DEFAULT_ICONS.info, 'qmb-ui-toast--info', true, options);
}
/**
 * Show a loading toast. Loader spinner icon, no dismiss button, persists until removed.
 * Call `toast.remove(id)` or `toast.dismiss(id)` with the returned ID to remove it.
 *
 * @example
 * const id = showLoadingToast('Processing...');
 * // later:
 * toast.remove(id);
 */
export function showLoadingToast(message, options) {
    return renderToast(message, 'loading', DEFAULT_ICONS.loading, undefined, false, options);
}
/**
 * Show a persistent progress toast: a title, a `ProgressBar` (suppressed for `variant: 'error'`),
 * and an optional action button. Reuses `ToastWrapper` (same `qmb-ui-toast` classing and dismiss
 * animation as every other variant here) and `react-hot-toast`'s own id-based upsert -- calling
 * this again with the same `id` updates the existing toast in place instead of stacking a new one.
 *
 * Deliberately calls the plain `toast()` entry point rather than `toast.custom()`: `toast.custom`
 * bypasses react-hot-toast's internal `ToastBar`, which is what supplies the `role="status"`
 * element `ToastWrapper`'s DOM tagging (`tagToastEl`/`findToastWrapper`) walks up to find. `toast()`
 * with a render-prop message takes the same "blank" rendering path as every other `show*Toast`
 * function here, so the upsert-by-id semantics are identical either way and the existing
 * class/animation plumbing keeps working unmodified.
 *
 * @example
 * const id = showProgressToast({
 *   id: 'building-attachments-upload',
 *   progress: 42,
 *   title: 'Uploading 3 attachments · 42%',
 *   action: { label: 'View progress', onClick: reopenDrawer }
 * });
 * // later, same id -> updates in place:
 * showProgressToast({ id: 'building-attachments-upload', progress: 78, title: '...' });
 * // when the batch resolves or the drawer reopens:
 * dismissToast(id);
 */
export function showProgressToast(options) {
    ensureToasterPositioning();
    const { id, progress, title, action, variant = 'progress' } = options;
    const isError = variant === 'error';
    // A dedicated class, not the shared `--error`: that one is the solid-red treatment used by
    // every `showToast.error` in the app, and restyling it here would change all of them.
    const variantClass = isError ? 'qmb-ui-toast--progress-error' : 'qmb-ui-toast--progress';
    const icon = isError ? DEFAULT_ICONS.error : DEFAULT_ICONS.loading;
    return toast((t) => (React.createElement(ToastWrapper, { t: t, variantClass: variantClass, displayDuration: Infinity },
        React.createElement("span", { className: clsx('notification__content', 'notification__content--progress', {
                'notification__content--progress-error': isError
            }) },
            React.createElement("span", { className: "notification__icon" }, icon),
            React.createElement("span", { className: "notification__progress-body" },
                React.createElement("span", { className: "notification__title" }, title),
                action && !isError && (React.createElement(Button, { variant: "secondary", className: "notification__action", onClick: action.onClick }, action.label))),
            action && isError && (React.createElement(Button, { variant: "danger", className: "notification__action--inline", onClick: action.onClick }, action.label))),
        !isError && React.createElement(ProgressBar, { value: progress, showLabel: false, className: "notification__progress-bar" }))), { id, duration: Infinity });
}
/**
 * Removes a toast immediately, without the manual-dismiss whoosh animation -- for callers (like
 * `showProgressToast` consumers) reacting to a state change rather than a user click.
 */
export function dismissToast(id) {
    toast.remove(id);
}
